import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useSearchParams } from 'react-router-dom';
import NavigationBar from '../../components/ui/NavigationBar';
import CourseGrid from '../course-catalog/components/CourseGrid';
import Icon from '../../components/AppIcon';
import { listCatalogListings } from '../../utils/catalogHelpers';

// Same shape the catalog page feeds into CourseGrid
const normalizeCourse = (listing) => ({
  id: listing?.courseId,
  courseId: listing?.courseId,
  orgId: listing?.providerId,
  title: listing?.title,
  description: listing?.description,
  category: listing?.category || 'Other',
  price: listing?.priceNesol || 0,
  isFree: listing?.isFree !== false,
  providerName: listing?.providerName || 'Unknown Provider',
  providerVerified: !!listing?.providerVerified,
  enrollmentType: listing?.enrollmentType || 'invite',
  moduleCount: listing?.moduleCount || 0,
  lessonCount: listing?.lessonCount || 0,
  thumbnail: listing?.thumbnailUrl || '',
  isLive: true,
});

// Every word the user typed must appear somewhere in the listing,
// so "artificial intelligence" matches even if the words are in different fields.
const matches = (listing, tokens) => {
  const haystack = [
    listing?.title,
    listing?.description,
    listing?.category,
    listing?.providerName,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return tokens.every((t) => haystack.includes(t));
};

const PathCard = ({ path }) => (
  <div className="rounded-xl border border-border bg-card p-5">
    <div className="mb-3 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
      <Icon name="Route" size={14} />
      <span>Program</span>
    </div>
    <h3 className="mb-1 text-base font-semibold text-foreground">{path?.title}</h3>
    <p className="mb-3 line-clamp-2 text-sm text-muted-foreground">{path?.description}</p>
    <p className="text-xs text-muted-foreground">
      {path?.nodeCount || 0} courses · {path?.providerName || 'Unknown Provider'}
    </p>
  </div>
);

const SearchPage = () => {
  const [searchParams] = useSearchParams();
  const q = (searchParams.get('q') || '').trim();
  const [listings, setListings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      const { listings: data } = await listCatalogListings();
      setListings(data || []);
      setIsLoading(false);
    };
    load();
  }, []);

  const { courses, paths } = useMemo(() => {
    const tokens = q.toLowerCase().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return { courses: [], paths: [] };
    const hits = listings.filter((l) => matches(l, tokens));
    return {
      courses: hits.filter((l) => l?.listingType !== 'path').map(normalizeCourse),
      paths: hits.filter((l) => l?.listingType === 'path'),
    };
  }, [listings, q]);

  const total = courses.length + paths.length;

  return (
    <>
      <Helmet>
        <title>{q ? `Search: ${q}` : 'Search'} - Nesolearn Platform</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <NavigationBar />
        <main className="mx-auto max-w-7xl px-4 pb-12 pt-28 sm:px-6 sm:pt-32 lg:px-8">
          <h1 className="mb-1 text-2xl font-bold text-foreground sm:text-3xl">
            {q ? `Results for "${q}"` : 'Search Nesolearn'}
          </h1>
          {!isLoading && q && (
            <p className="mb-8 text-sm text-muted-foreground">
              {total} {total === 1 ? 'result' : 'results'}
            </p>
          )}

          {!q && !isLoading && (
            <p className="text-muted-foreground">Type a course or program name in the search bar.</p>
          )}

          {q && !isLoading && total === 0 && (
            <div className="rounded-xl border border-border bg-card p-12 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-accent/10">
                <Icon name="Search" size={24} className="text-accent" />
              </div>
              <h2 className="mb-1 text-lg font-semibold text-foreground">No results for "{q}"</h2>
              <p className="text-sm text-muted-foreground">
                Try a different keyword, or browse the full course catalog.
              </p>
            </div>
          )}

          {(isLoading || courses.length > 0) && q && (
            <section className="mb-10">
              <h2 className="mb-4 text-lg font-semibold text-foreground">Courses</h2>
              <CourseGrid courses={courses} isLoading={isLoading} />
            </section>
          )}

          {paths.length > 0 && (
            <section>
              <h2 className="mb-4 text-lg font-semibold text-foreground">Programs</h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {paths.map((p) => (
                  <PathCard key={p.pathId} path={p} />
                ))}
              </div>
            </section>
          )}
        </main>
      </div>
    </>
  );
};

export default SearchPage;